import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { reactDnDTreeView } from '@yola/ws-ui';
import useTreeViewData from '../../hooks/use-tree-view-data';
import getDragItemIds, {
  getDraggedItemsCount,
  isNodeInSelection,
} from '../../helpers/get-drag-item-ids';
import getRangeSelectionPageIds from '../../helpers/get-range-selection-page-ids';
import TreeViewItemContainer from './tree-view-item-container.js';
import TreeViewItemDragPreview from './tree-view-item-drag-preview.js';

const { Tree } = reactDnDTreeView;

// `dropTarget` is keyed to the library's `isOver`, which — with dropTargetOffset
// at 0 — only fires when hovering a node's body (drop into a folder) or the root
// list's own padding (drop to root), so the highlight always matches the drop.
const treeClasses = {
  root: 'ws-page-manager-tree-view-list--root',
  container: 'ws-page-manager-tree-view-list',
  listItem: 'ws-page-manager-tree-view-item-container',
  draggingSource: 'ws-page-manager-tree-view-item-container--dragging',
  dropTarget: 'ws-page-manager-tree-view-item-container--drop-target',
};

// Pages may occupy up to the 4th level (a page inside a 3rd-level folder),
// but folders themselves may only nest 3 levels deep.
const MAX_NESTING_LEVEL = 4;
const MAX_FOLDER_DEPTH = 3;

// An incidental micro-drag (a 1px slip or a slightly long mouse down) travels
// only a few px, while a deliberate drag travels at least this far.
const CLEAR_SELECTION_DRAG_DISTANCE = 10;

const PageManagerTreeView = ({
  rootId,
  items,
  savedOpenFolderIds = null,
  defaultOpenFolderIdsOnSearch = null,
  searchQuery = '',
  selectModeEnabled = false,
  selectedItemsMap = {},
  folderSelectionStateById = {},
  onItemSelect = Function.prototype,
  onClearSelection = Function.prototype,
  onChange = Function.prototype,
  onItemMove = Function.prototype,
  onOpenNodeIdsChange = Function.prototype,
  onItemClick = Function.prototype,
  onStartItemDrag = Function.prototype,
  onEndItemDrag = Function.prototype,
}) => {
  const treeRef = useRef(null);
  const lastSelectedIdRef = useRef(null);
  const stopDragTrackingRef = useRef(null);

  const { openNodeIds, itemsDepthMap, descendantItemIds, visibleOrderIds, setOpenNodeIds } =
    useTreeViewData(items, rootId, savedOpenFolderIds, defaultOpenFolderIdsOnSearch, searchQuery);

  // Root pages are non-droppable, so hovering one already falls through to the
  // root list (drop to root). Folders are droppable and capture the hover, so a
  // folders-only root needs the padding strip to expose a root drop area.
  const rootHasDirectPages = useMemo(
    () => items.some((node) => node.parent === rootId && !node.droppable),
    [items, rootId]
  );

  const nodeById = useMemo(
    () =>
      items.reduce((acc, node) => {
        acc[node.id] = node;
        return acc;
      }, {}),
    [items]
  );

  const folderIds = useMemo(
    () => new Set(items.filter((node) => node.droppable).map((node) => node.id)),
    [items]
  );

  const resolveDragItemIds = useCallback(
    (dragSourceId) => getDragItemIds(dragSourceId, { selectModeEnabled, selectedItemsMap, items }),
    [selectModeEnabled, selectedItemsMap, items]
  );

  // True when `candidateId` sits inside the subtree rooted at `ancestorId`.
  const isDescendantOf = useCallback(
    (candidateId, ancestorId) => {
      let parentId = nodeById[candidateId]?.parent;

      while (parentId != null && parentId !== rootId) {
        if (parentId === ancestorId) return true;
        parentId = nodeById[parentId]?.parent;
      }

      return false;
    },
    [nodeById, rootId]
  );

  const handleItemSelect = useCallback(
    (id, shiftKey) => {
      if (shiftKey && lastSelectedIdRef.current && lastSelectedIdRef.current !== id) {
        const pageIds = getRangeSelectionPageIds({
          visibleOrderIds,
          anchorId: lastSelectedIdRef.current,
          targetId: id,
          folderIds,
          expandedFolderIds: openNodeIds,
          descendantItemIds,
          selectedItemsMap,
        });

        if (pageIds) {
          pageIds.forEach(onItemSelect);
          lastSelectedIdRef.current = id;
          return;
        }
      }

      onItemSelect(id);
      lastSelectedIdRef.current = id;
    },
    [visibleOrderIds, selectedItemsMap, onItemSelect, descendantItemIds, folderIds, openNodeIds]
  );

  const handleDrop = useCallback(
    (_, { dragSourceId, dropTargetId }) => {
      const newParentId = dropTargetId == null ? rootId : dropTargetId;
      const itemIds = resolveDragItemIds(dragSourceId).filter(
        (id) => nodeById[id]?.parent !== newParentId
      );

      if (!itemIds.length) return;

      onItemMove({ itemIds, newParentId });
    },
    [onItemMove, rootId, resolveDragItemIds, nodeById]
  );

  const handleChangeOpen = useCallback(
    (newOpenIds) => {
      setOpenNodeIds(newOpenIds);
      onOpenNodeIdsChange(newOpenIds);

      const updatedFlatTree = items.map((node) => {
        if (!node?.droppable) return node;

        return {
          ...node,
          collapsed: !newOpenIds.includes(node?.data.id),
        };
      });

      onChange(updatedFlatTree);
    },
    [setOpenNodeIds, onOpenNodeIdsChange, items, onChange]
  );

  const handleItemClick = useCallback(
    (item) => {
      onItemClick(item);
    },
    [onItemClick]
  );

  const handleDragStart = useCallback(
    (node) => {
      if (node.data) {
        onStartItemDrag(node.data);
      }

      // Dragging a row that isn't part of the selection moves only that row, so
      // drop the unrelated selection to keep the gesture and its result in sync.
      // The clearing only kicks in once the pointer has travelled far enough
      // from where the drag started, so an incidental micro-drag doesn't wipe
      // the selection.
      if (selectModeEnabled && !isNodeInSelection(node.id, { selectedItemsMap, items })) {
        let origin = null;

        const onDragMove = (event) => {
          const { clientX, clientY } = event.touches?.[0] || event;

          if (!origin) {
            origin = { x: clientX, y: clientY };
            return;
          }

          const distance = Math.hypot(clientX - origin.x, clientY - origin.y);

          if (distance >= CLEAR_SELECTION_DRAG_DISTANCE) {
            stopDragTrackingRef.current?.();
            onClearSelection();
          }
        };

        stopDragTrackingRef.current = () => {
          window.removeEventListener('dragover', onDragMove);
          window.removeEventListener('touchmove', onDragMove);
          stopDragTrackingRef.current = null;
        };

        window.addEventListener('dragover', onDragMove);
        window.addEventListener('touchmove', onDragMove);
      }
    },
    [onStartItemDrag, selectModeEnabled, selectedItemsMap, items, onClearSelection]
  );

  const handleDragEnd = useCallback(
    (node) => {
      stopDragTrackingRef.current?.();

      if (node.data) {
        onEndItemDrag(node.data);
      }
    },
    [onEndItemDrag]
  );

  const canDrag = useCallback((node) => node.draggable, []);

  const canDrop = useCallback(
    (_, options) => {
      const { dragSource, dropTarget, dragSourceId, dropTargetId } = options;

      if (!dragSource || dragSourceId === undefined) {
        return false;
      }

      const isRootDrop = dropTargetId === rootId || dropTargetId == null;
      const effectiveDropParentId = isRootDrop ? rootId : dropTargetId;

      // In select mode a drag carries the whole selection, so every check below
      // is evaluated against the full set of items being moved.
      const dragItemIds = resolveDragItemIds(dragSourceId);

      // Can't drop onto one of the dragged items or into a dragged folder's subtree.
      if (dragItemIds.includes(dropTargetId)) {
        return false;
      }

      if (!isRootDrop && dragItemIds.some((id) => isDescendantOf(dropTargetId, id))) {
        return false;
      }

      // At least one item has to actually change its parent.
      const movesSomething = dragItemIds.some(
        (id) => nodeById[id]?.parent !== effectiveDropParentId
      );
      if (!movesSomething) {
        return false;
      }

      if (isRootDrop) {
        return true;
      }

      if (!dropTarget?.droppable) {
        return false;
      }

      const dropTargetEntry = itemsDepthMap.get(dropTargetId);
      if (!dropTargetEntry) return false;

      const dropDepth = dropTargetEntry.depth + 1;

      // All-or-nothing: every dragged subtree must fit within the depth limits.
      return dragItemIds.every((id) => {
        const dragDepthEntry = itemsDepthMap.get(id);
        if (!dragDepthEntry) return false;

        // The whole subtree (pages included) must fit within the overall limit.
        if (dropDepth + dragDepthEntry.maxChildDepth > MAX_NESTING_LEVEL) {
          return false;
        }

        // Folders within the dragged subtree must not exceed the folder limit.
        const { maxFolderChildDepth } = dragDepthEntry;
        if (maxFolderChildDepth != null && dropDepth + maxFolderChildDepth > MAX_FOLDER_DEPTH) {
          return false;
        }

        return true;
      });
    },
    [itemsDepthMap, rootId, resolveDragItemIds, isDescendantOf, nodeById]
  );

  const renderCallback = useCallback(
    (node, options) => (
      <TreeViewItemContainer
        {...options}
        node={node}
        treeRef={treeRef}
        searchQuery={searchQuery}
        selectModeEnabled={selectModeEnabled}
        selectedItemsMap={selectedItemsMap}
        folderSelectionStateById={folderSelectionStateById}
        onItemSelect={handleItemSelect}
        onItemClick={handleItemClick}
      />
    ),
    [
      handleItemClick,
      searchQuery,
      selectModeEnabled,
      selectedItemsMap,
      folderSelectionStateById,
      handleItemSelect,
    ]
  );

  const dragPreviewRenderCallback = useCallback(
    (monitorProps) => {
      const dragSourceId = monitorProps.item?.id;
      const count =
        dragSourceId == null
          ? 1
          : getDraggedItemsCount(dragSourceId, { selectModeEnabled, selectedItemsMap, items });

      return <TreeViewItemDragPreview {...monitorProps} count={count} />;
    },
    [selectModeEnabled, selectedItemsMap, items]
  );

  useEffect(() => {
    if (!selectModeEnabled) {
      lastSelectedIdRef.current = null;
    }
  }, [selectModeEnabled]);

  return (
    <div
      className={classNames('ws-page-manager-tree-view', {
        'ws-page-manager-tree-view--root-folders-only': !rootHasDirectPages,
      })}
      data-drag-backend="multibackend"
    >
      <Tree
        ref={treeRef}
        tree={items}
        rootId={rootId}
        classes={treeClasses}
        initialOpen={openNodeIds}
        sort={false}
        insertDroppableFirst={false}
        dropTargetOffset={0}
        onDrop={handleDrop}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        canDrop={canDrop}
        canDrag={canDrag}
        onChangeOpen={handleChangeOpen}
        render={renderCallback}
        dragPreviewRender={dragPreviewRenderCallback}
      />
    </div>
  );
};

PageManagerTreeView.propTypes = {
  rootId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func,
  onItemMove: PropTypes.func,
  onOpenNodeIdsChange: PropTypes.func,
  onItemClick: PropTypes.func,
  savedOpenFolderIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  defaultOpenFolderIdsOnSearch: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  searchQuery: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectModeEnabled: PropTypes.bool,
  selectedItemsMap: PropTypes.shape({}),
  folderSelectionStateById: PropTypes.shape({}),
  onItemSelect: PropTypes.func,
  onClearSelection: PropTypes.func,
  onStartItemDrag: PropTypes.func,
  onEndItemDrag: PropTypes.func,
};

export default PageManagerTreeView;
